<template>
  <div class="app">
    <div v-if="isLoading" class="flex items-center justify-center p-5">
      <div v-if="!authError" class="loading-state">
        <div class="loading-spinner"></div>
        <div v-if="authStep" class="auth-step-label">{{ authStep }}</div>
      </div>
      <div v-else class="auth-error-state">
        <div class="auth-error-icon">!</div>
        <div class="auth-error-message">{{ authError }}</div>
        <button class="auth-retry-button" @click="runAuthFlow">Повторить</button>
      </div>
    </div>
    <div v-else>
      <template v-if="isAdmin">
        <PromoEdumotionModal />
        <UTabs 
          :tabs="tabs" 
          :activeTab="tab"
          @onChoseTab="onChoseTab"
        />

        <CoursesPage v-if="tab === 'courses'" />
        <StatisticsPage v-if="tab === 'statistics'" />
        <SettingsPage v-if="tab === 'settings'" />
      </template>
      <template v-else>
        <CoursesPage />
      </template>
    </div>
  </div>
</template>

<script setup>
import PromoEdumotionModal from './components/PromoEdumotionModal.vue'
import {ref, computed, watch, onMounted} from 'vue'
import { usePortalStore } from './stores/portalStore'
import api from './services/api'

// Only allow env tokens in development mode to prevent secrets in production bundles
const ENV_TOKEN = process.env.NODE_ENV === 'development'
  ? (process.env.VUE_APP_AUTH_TOKEN || process.env.VUE_APP_JWT_TOKEN || '')
  : ''

const AUTH_TIMEOUT = 10_000

const STEPS = {
  LOCAL_TOKEN: 'Проверка сохранённого токена...',
  ENV_TOKEN: 'Проверка токена окружения...',
  BX24_AUTH: 'Авторизация через Bitrix24...',
  BOOTSTRAP: 'Загрузка данных портала...',
}

const withTimeout = (promise, ms = AUTH_TIMEOUT, label = '') => {
  let timer
  return Promise.race([
    promise.finally(() => clearTimeout(timer)),
    new Promise((_, reject) => {
      timer = setTimeout(
        () => reject(new Error(`Таймаут${label ? ` (${label})` : ''}`)),
        ms
      )
    })
  ])
}

const toUserMessage = (error) => {
  const msg = error?.message || ''
  if (msg.includes('Таймаут')) return 'Сервер не отвечает'
  if (msg.includes('Network Error') || msg.includes('ERR_NETWORK')) return 'Нет соединения с сервером'
  if (error?.response?.status === 401 || error?.response?.status === 403) return 'Ошибка авторизации'
  return `Произошла ошибка: ${msg || 'неизвестная ошибка'}`
}

import CoursesPage from '@/components/page/CoursesPage.vue'
import StatisticsPage from '@/components/page/StatisticsPage.vue'
import SettingsPage from '@/components/page/SettingsPage.vue'
import UTabs from '@/components/ui/UTabs.vue'

const portalStore = usePortalStore()
const isLoading = ref(true)
const authStep = ref('')
const authError = ref(null)

const portalData = computed(() => portalStore.portalData.portal)
const isAdmin = computed(() => {
  const pd = portalStore.portalData || {}
  const byFlag = Boolean(pd.isAdmin)
  const admins = Array.isArray(pd?.info?.admins) ? pd.info.admins : []
  const uid = String(pd?.user?.ID ?? pd?.user?.id ?? '')
  return byFlag || (uid ? admins.includes(uid) : false)
})

const tab = ref('courses')
const tabs = computed(() => {
  const resTabs = [{ tab: 'courses', title: 'Мои курсы' }];

  if (isAdmin.value) {
    resTabs.push({ tab: 'statistics', title: 'Статистика' });
    resTabs.push({ tab: 'settings', title: 'Настройки' });
  }

  return resTabs;
})

const updateLoadingState = () => {
  const id = String(portalData.value?.id || '')
  isLoading.value = id.length === 0
}

const onChoseTab = (newTab) => {
  tab.value = newTab
}

updateLoadingState()

watch(() => String(portalStore.portalData?.portal?.id || ''), updateLoadingState)

// Пересчитываем права админа при изменении информации портала/пользователя
watch(
  () => ({
    admins: portalStore.portalData?.info?.admins,
    user: portalStore.portalData?.user,
  }),
  (v) => {
    try {
      const admins = Array.isArray(v?.admins) ? v.admins : []
      const uid = String((v?.user?.ID ?? v?.user?.id) || '')
      if (uid) {
        const effectiveAdmin = admins.includes(uid) || Boolean(portalStore.portalData?.isAdmin)
        portalStore.setIsAdmin(effectiveAdmin)
      }
    } catch (e) {
      console.error('[Auth] adminWatch:', e?.message || e, { error: e })
    }
  },
  { deep: true, immediate: true }
)

const parseSearch = () => {
  try { return new URLSearchParams(window.location.search) } catch { return new URLSearchParams('') }
}

const tryBitrixSignIn = async () => {
  const qs = parseSearch()
  let access_token = ''
  let member_id = ''
  let domain = qs.get('DOMAIN') || qs.get('domain') || ''
  let client_endpoint = ''

  const w = window
  if (w && w.BX24 && typeof w.BX24.callMethod === 'function') {
    try {
      const auth = (w.BX24.getAuth && w.BX24.getAuth()) || {}
      access_token = auth?.access_token || auth?.AUTH_ID || ''
      client_endpoint = auth?.client_endpoint || ''
      const appInfo = await withTimeout(new Promise((resolve, reject) => {
        w.BX24.callMethod('app.info', {}, (res) => {
          if (res && typeof res.error === 'function' && res.error()) return reject(res.error())
          resolve((res && res.answer && res.answer.result) || {})
        })
      }), AUTH_TIMEOUT, 'BX24.callMethod')
      member_id = appInfo?.member_id || auth?.member_id || ''
      domain = domain || auth?.domain || ''
    } catch (e) {
      console.error('[Auth] BX24.callMethod:', e?.message || e, { error: e, endpoint: 'app.info' })
    }
  }

  if (!access_token || !member_id) return false

  const resp = await withTimeout(
    api.bitrixSignIn({ access_token, member_id, domain, client_endpoint }),
    AUTH_TIMEOUT,
    'bitrixSignIn'
  )
  const token = resp?.token || ''
  if (token) {
    api.setAuthToken(token)
    console.log('[Auth] BX24 sign-in:', 'Успешно', { tokenLength: token.length })
  }
  const user = resp?.user || {}
  // Store minimal portal info so UI can stop loading
  portalStore.setPortalData({
    portal: { id: resp?.portalId || '', domain: resp?.domain || '' },
  })
  portalStore.setUser(user)
  portalStore.setIsAdmin(Boolean(user?.is_admin))
  // Дополнительно подтянем полную информацию портала и список пользователей из API (BitrixUser)
  try {
    const info = await withTimeout(api.getPortal(), AUTH_TIMEOUT, 'getPortal')
    if (info && typeof info === 'object') {
      portalStore.setPortalData({ info })
    }
  } catch (e) {
    console.error('[Auth] getPortal:', e?.message || e, { error: e })
  }
  try {
    const users = await withTimeout(api.getBitrixUsers(), AUTH_TIMEOUT, 'getBitrixUsers')
    if (Array.isArray(users)) {
      portalStore.setAllUsers(users)
    }
  } catch (e) {
    console.error('[Auth] getBitrixUsers:', e?.message || e, { error: e })
  }
  try {
    const admins = Array.isArray(portalStore.portalData?.info?.admins)
      ? portalStore.portalData.info.admins
      : []
    const uid = String((user?.id ?? user?.ID) || '')
    const effectiveAdmin = Boolean(user?.is_admin) || (uid && admins.includes(uid))
    portalStore.setIsAdmin(effectiveAdmin)
  } catch (e) {
    console.error('[Auth] adminCheck:', e?.message || e, { error: e })
  }
  return true
}

const decodeJwt = (t) => {
  try {
    const parts = t.split('.')
    if (parts.length < 3) return {}
    const s = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(s)
  } catch { return {} }
}

const getLocalToken = () => {
  try { return localStorage.getItem('jwt_token') || '' } catch { return '' }
}

const bootstrapWithToken = async (token) => {
  if (!token) return false
  console.log('[Auth] bootstrapWithToken:', 'Начало', { tokenLength: token.length })
  api.setAuthToken(token)
  const payload = decodeJwt(token)
  // Устанавливаем минимальные данные портала сразу, чтобы снять лоадер даже при временных ошибках API
  portalStore.setPortalData({
    portal: { id: String(payload?.portalId || ''), domain: String(payload?.domain || '') },
  })
  try {
    const info = await withTimeout(api.getPortal(), AUTH_TIMEOUT, 'getPortal')
    if (info && typeof info === 'object') {
      portalStore.setPortalData({
        portal: { id: info.id || '', domain: info.domain || '' },
        info,
      })
    }
  } catch (e) {
    console.error('[Auth] bootstrapWithToken.getPortal:', e?.message || e, { error: e })
  }
  try {
    const users = await withTimeout(api.getBitrixUsers(), AUTH_TIMEOUT, 'getBitrixUsers')
    if (Array.isArray(users)) {
      portalStore.setAllUsers(users)
      const uid = String(payload?.bitrixUserId || '')
      if (uid) {
        const u = users.find((x) => String(x?.ID || '') === uid)
        if (u) portalStore.setUser(u)
      }
    }
  } catch (e) {
    console.error('[Auth] bootstrapWithToken.getBitrixUsers:', e?.message || e, { error: e })
  }
  try {
    const admins = Array.isArray(portalStore.portalData?.info?.admins)
      ? portalStore.portalData.info.admins
      : []
    const uid = String(payload?.bitrixUserId || '')
    const effectiveAdmin = Boolean(payload?.is_admin) || (uid && admins.includes(uid))
    portalStore.setIsAdmin(effectiveAdmin)
  } catch (e) {
    console.error('[Auth] bootstrapWithToken.adminCheck:', e?.message || e, { error: e })
  }
  return true
}

const runAuthFlow = async () => {
  authError.value = null
  authStep.value = ''
  isLoading.value = true

  try {
    // Step 1: localStorage token
    authStep.value = STEPS.LOCAL_TOKEN
    const localToken = getLocalToken()
    if (localToken) {
      console.log('[Auth] localStorage:', 'Токен найден', { tokenLength: localToken.length })
      const ok = await bootstrapWithToken(localToken)
      if (ok) {
        console.log('[Auth] localStorage:', 'Авторизация успешна')
        return
      }
    } else {
      console.log('[Auth] localStorage:', 'Токен не найден')
    }

    // Step 2: ENV token
    authStep.value = STEPS.ENV_TOKEN
    if (ENV_TOKEN) {
      console.log('[Auth] envToken:', 'Токен найден', { tokenLength: ENV_TOKEN.length })
      const ok = await bootstrapWithToken(ENV_TOKEN)
      if (ok) {
        console.log('[Auth] envToken:', 'Авторизация успешна')
        return
      }
    } else {
      console.log('[Auth] envToken:', 'Токен не задан')
    }

    // Step 3: BX24 OAuth
    authStep.value = STEPS.BX24_AUTH
    const ok = await tryBitrixSignIn()
    if (ok) {
      console.log('[Auth] BX24:', 'Авторизация успешна')
      return
    }

    throw new Error('Авторизация Bitrix24 недоступна')
  } catch (e) {
    console.error('[Auth] Ошибка:', e?.message || e, { error: e })
    authError.value = toUserMessage(e)
  }
}

onMounted(() => runAuthFlow())

</script>

<style>
.app {
  max-width: 1200px;
  margin: 0 auto;
}

h1 {
  color: #333;
  margin-bottom: 20px;
}

/* Мобильная адаптивность */
@media (max-width: 768px) {
  .app {
    max-width: 100%;
    margin: 0;
    padding: 0;
  }
  
  h1 {
    font-size: 1.5rem;
    margin-bottom: 16px;
    padding: 0 16px;
  }
  
  .loader {
    min-height: 150px;
    font-size: 18px;
    padding: 0 16px;
  }
}

@media (max-width: 480px) {
  h1 {
    font-size: 1.25rem;
    margin-bottom: 12px;
    padding: 0 12px;
  }
  
  .loader {
    min-height: 120px;
    font-size: 16px;
    padding: 0 12px;
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 0;
  color: #6b7280;
}

.loading-spinner {
  width: 100px;
  height: 100px;
  border: 10px solid #e5e7eb;
  border-left: 10px solid #574AEA;
  border-radius: 50%;
  animation: spin 1.5s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.auth-step-label {
  margin-top: 16px;
  font-size: 14px;
  color: #6b7280;
  text-align: center;
}

.auth-error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
}

.auth-error-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: #fef2f2;
  color: #ef4444;
  font-size: 32px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.auth-error-message {
  font-size: 16px;
  color: #374151;
  margin-bottom: 24px;
  max-width: 400px;
  line-height: 1.5;
}

.auth-retry-button {
  padding: 12px 32px;
  background-color: #574AEA;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.auth-retry-button:hover {
  background-color: #4338ca;
}

.auth-retry-button:active {
  background-color: #3730a3;
}
</style>
